if (!customElements.get('cookie-banner')) {
  customElements.define(
    'cookie-banner',
    class CookieBanner extends ModalElement {
      constructor() {
        super();
    
        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }
    
      get acceptButton() {
        return this.querySelector('button[name="accept"]');
      }
    
      get declineButton() {
        return this.querySelector('button[name="decline"]');
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;
    
        if (this.testMode) {
          this.load(this.delay);
          return;
        }
    
        if (window.Shopify?.customerPrivacy) {
          this.onConsentLibraryLoaded();
          return;
        }

        try {
          window.Shopify?.loadFeatures?.([{
            name: 'consent-tracking-api',
            version: '0.1',
            onLoad: this.onConsentLibraryLoaded.bind(this)
          }]);
        }
        catch {
          this.onConsentLibraryLoaded(true);
        }
      }
    
      load(delay) {
        if (Shopify.designMode) return;

        this.bindPolicyButtons();
        setTimeout(() => {
          if (this.testMode || this.shouldShowBanner()) this.show();
        }, delay * 1000);
      }

      bindPolicyButtons() {
        if (this.buttonsBound) return;
        this.buttonsBound = true;
        if (this.acceptButton) this.on(this.acceptButton, 'click', this.acceptPolicy.bind(this));
        if (this.declineButton) this.on(this.declineButton, 'click', this.declinePolicy.bind(this));
      }
    
      acceptPolicy() {
        this.saveConsent(true);
      }
    
      declinePolicy() {
        this.saveConsent(false);
      }

      saveConsent(consent) {
        if (this.testMode || Shopify.designMode) {
          this.hide();
          return;
        }
        if (this.savingConsent) return;

        const errorMessage = this.querySelector('[data-consent-error]');
        if (errorMessage) errorMessage.hidden = true;
        this.setSavingConsent(true);

        let finished = false;
        const finish = (result) => {
          if (finished) return;
          finished = true;
          clearTimeout(timeout);
          this.setSavingConsent(false);
          if (result?.error) {
            if (errorMessage) errorMessage.hidden = false;
            return;
          }
          this.hide();
          this.notifyConsentUpdated();
        };
        const timeout = setTimeout(() => finish({ error: true }), 15000);

        try {
          const privacy = window.Shopify?.customerPrivacy;
          if (typeof privacy?.setTrackingConsent !== 'function') {
            finish({ error: true });
            return;
          }
          privacy.setTrackingConsent(consent, finish);
        }
        catch {
          finish({ error: true });
        }
      }

      setSavingConsent(saving) {
        this.savingConsent = saving;
        this.setAttribute('aria-busy', saving ? 'true' : 'false');
        if (this.acceptButton) this.acceptButton.disabled = saving;
        if (this.declineButton) this.declineButton.disabled = saving;
      }

      shouldShowBanner() {
        const privacy = window.Shopify?.customerPrivacy;
        try {
          if (typeof privacy?.shouldShowBanner === 'function') return privacy.shouldShowBanner();
          return privacy?.userCanBeTracked?.() === false && privacy?.getTrackingConsent?.() === 'no_interaction';
        }
        catch {
          document.documentElement.classList.remove('dope-cookie-banner-ready');
          return false;
        }
      }

      onConsentLibraryLoaded(error) {
        const privacy = window.Shopify?.customerPrivacy;
        if (error || typeof privacy?.setTrackingConsent !== 'function' || !this.acceptButton || !this.declineButton) return;
        if (typeof privacy.shouldShowBanner !== 'function' &&
          (typeof privacy.userCanBeTracked !== 'function' || typeof privacy.getTrackingConsent !== 'function')) return;

        this.bindPolicyButtons();
        // Leave Shopify's banner available until this replacement can record a choice.
        document.documentElement.classList.add('dope-cookie-banner-ready');
        if (this.shouldShowBanner()) {
          this.load(this.delay);
        }
      }
    
      notifyConsentUpdated() {
        document.dispatchEvent(new CustomEvent('theme:consent-updated'));
      }
    
      afterHide() {
        super.afterHide();
        document.body.classList.remove('has-cookie-banner');
      }
      
      afterShow() {
        super.afterShow();
        document.body.classList.add('has-cookie-banner');
      }
    }
  );
}
